import React, { useState } from "react";
import { PiggyBank, BarChart3, Eye, ShieldCheck, GraduationCap, Sparkles, Globe2, User as UserIcon, Microscope, Hammer, LayoutGrid, Filter, Radar, Shield, Vault as VaultIcon, Compass } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import LoginButton from "./LoginButton";
import Logo from "./Logo";
import { ProUpgradeButton, ProUpgradeModal } from "./ProUpgrade";
import { useAuth } from "@/contexts/AuthContext";

export const Header = () => {
  const { user } = useAuth();
  const [proOpen, setProOpen] = useState(false);
  const location = useLocation();
  const isEquities = location.pathname.startsWith("/equities");
  const isSmart = location.pathname.startsWith("/smart-money");
  const isSignals = location.pathname.startsWith("/signals");
  const isAdmin = location.pathname.startsWith("/admin");
  const isAccount = location.pathname.startsWith("/account");
  const isLearn = location.pathname.startsWith("/learn");
  const isAnalysis = location.pathname.startsWith("/analysis");
  const isForge = location.pathname.startsWith("/forge");
  const isWorkspace = location.pathname.startsWith("/workspace");
  const isVault = location.pathname.startsWith("/vault");
  const isMarketsPro = location.pathname.startsWith("/markets-pro");
  const isMacroSignals = location.pathname.startsWith("/smart-money/macro");
  const isFutures = location.pathname === "/futures" || location.pathname === "/dashboard";
  const isMarkets = location.pathname === "/" || location.pathname.startsWith("/markets");

  // Learn now lives at /learn (a full menu page); remove the in-page scroll behavior.

  // Per-route neon accent palette. Each item gets a custom glow so the nav
  // feels like a vibrant control surface instead of generic gray pills.
  // Order: learn · markets · analysis · macro · markets pro · forge · vault ·
  // futures · stocks · workspace · smart money · signals
  const navItems = [
    { to: "/learn", label: "Learn", short: "Learn", icon: Compass, active: isLearn, testid: "page-nav-learn", neon: "#FF49B0" },
    { to: "/markets", label: "Markets", icon: Globe2, active: isMarkets, testid: "page-nav-markets", neon: "#3FE5DC" },
    { to: "/analysis", label: "Analysis", icon: Microscope, active: isAnalysis, testid: "page-nav-analysis", neon: "#00E5FF" },
    { to: "/smart-money/macro", label: "Macro", icon: Radar, active: isMacroSignals, testid: "page-nav-macro", neon: "#A98CFF" },
    { to: "/markets-pro", label: "Markets Pro", short: "Pro", icon: Filter, active: isMarketsPro, testid: "page-nav-markets-pro", neon: "#FFD60A" },
    { to: "/forge", label: "Forge", icon: Hammer, active: isForge, testid: "page-nav-forge", neon: "#FF7A33" },
    { to: "/vault", label: "Vault", icon: VaultIcon, active: isVault, testid: "page-nav-vault", neon: "#FFD60A" },
    { to: "/futures", label: "Futures", icon: BarChart3, active: isFutures, testid: "page-nav-futures", neon: "#00C805" },
    { to: "/equities", label: "Stocks & ETFs", short: "Stocks", icon: PiggyBank, active: isEquities, testid: "page-nav-equities", neon: "#FFD60A" },
    { to: "/workspace", label: "Workspace", short: "Multi", icon: LayoutGrid, active: isWorkspace, testid: "page-nav-workspace", neon: "#3FE5DC" },
    { to: "/smart-money", label: "Smart Money", short: "Smart$", icon: Eye, active: isSmart && !isMacroSignals, testid: "page-nav-smart-money", neon: "#A98CFF" },
    { to: "/signals", label: "Signals", icon: Sparkles, active: isSignals, testid: "page-nav-signals", neon: "#FF7A33" },
  ];

  return (
    <>
      <header
        className="fixed top-0 inset-x-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-white/10 safe-top"
        data-testid="site-header"
      >
        {/* Top row — brand + actions */}
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand">
            <Logo size={26} withFrame />
            <div className="flex flex-col items-center leading-none">
              <span
                className="text-[13.5px] font-semibold tracking-tight"
                style={{ color: "#C084FC", textShadow: "0 0 10px #A855F780" }}
              >
                Gamma
              </span>
              <span className="text-[8.5px] uppercase tracking-[0.18em] text-white/40 mt-0.5">
                trade <span className="text-[#A855F7]/60 mx-0.5">·</span> invest
              </span>
            </div>
          </Link>

          {/* Desktop page nav — neon glow on active */}
          <nav className="hidden md:flex items-center gap-1 text-sm" data-testid="page-nav">
            {navItems.map(({ to, label, icon: Icon, active, testid, neon }) => (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                style={
                  active
                    ? {
                        color: neon,
                        borderColor: `${neon}55`,
                        background: `${neon}10`,
                        boxShadow: `0 0 14px ${neon}40, inset 0 0 8px ${neon}15`,
                        textShadow: `0 0 8px ${neon}80`,
                      }
                    : { ["--neon-hover"]: neon }
                }
                className={
                  "neon-link inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition text-[12px] font-medium " +
                  (active
                    ? "border"
                    : "text-white/70 hover:text-white border border-transparent")
                }
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </Link>
            ))}
          </nav>

          {/* Right side: status + buttons */}
          <div className="flex items-center gap-2 sm:gap-3" data-testid="header-status">
            <span className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/60">
              <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-[#00C805] live-dot" />
              Live
            </span>
            {user?.is_admin && (
              <Link
                to="/admin"
                data-testid="header-admin-link"
                className="hidden md:inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-[#A98CFF]/85 hover:text-[#A98CFF] transition"
                title="Admin dashboard"
              >
                Admin
              </Link>
            )}
            {user && (
              <Link
                to="/account"
                data-testid="header-account-link"
                className="hidden md:inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-white/65 hover:text-white transition"
                title="My account"
              >
                <UserIcon className="w-3 h-3" /> Account
              </Link>
            )}
            {user && <ProUpgradeButton onOpen={() => setProOpen(true)} />}
            <LoginButton />
          </div>
        </div>

        {/* Mobile secondary row — horizontal-scroll tab strip with edge fade + neon active */}
        <div
          className="md:hidden border-t border-white/[0.06] bg-[#050505]/85 backdrop-blur-xl relative"
          data-testid="mobile-tab-bar"
        >
          {/* Right edge fade hint that there's more to scroll */}
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#050505] to-transparent z-10" />
          <nav
            className="flex items-center gap-1 px-3 pr-10 py-1.5 overflow-x-auto no-scrollbar"
            aria-label="Primary"
          >
            {navItems.map(({ to, label, short, icon: Icon, active, testid, neon }) => (
              <Link
                key={to}
                to={to}
                data-testid={`${testid}-mobile`}
                style={
                  active
                    ? {
                        color: neon,
                        borderColor: `${neon}55`,
                        background: `${neon}10`,
                        boxShadow: `0 0 12px ${neon}50, inset 0 0 6px ${neon}20`,
                        textShadow: `0 0 6px ${neon}90`,
                      }
                    : undefined
                }
                className={
                  "shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition text-[11.5px] font-medium " +
                  (active
                    ? "border"
                    : "text-white/70 hover:text-white border border-transparent")
                }
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="whitespace-nowrap">{short || label}</span>
              </Link>
            ))}
            {user && (
              <Link
                to="/account"
                data-testid="header-account-link-mobile"
                style={
                  isAccount
                    ? {
                        color: "#3FE5DC",
                        borderColor: "#3FE5DC55",
                        background: "#3FE5DC10",
                        boxShadow: "0 0 12px #3FE5DC50, inset 0 0 6px #3FE5DC20",
                        textShadow: "0 0 6px #3FE5DC90",
                      }
                    : undefined
                }
                className={
                  "shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition text-[11.5px] " +
                  (isAccount ? "border" : "text-white/70 hover:text-white border border-transparent")
                }
              >
                <UserIcon className="w-3.5 h-3.5" /> Account
              </Link>
            )}
            {user?.is_admin && (
              <Link
                to="/admin"
                data-testid="header-admin-link-mobile"
                style={
                  isAdmin
                    ? {
                        color: "#A98CFF",
                        borderColor: "#A98CFF55",
                        background: "#A98CFF10",
                        boxShadow: "0 0 12px #A98CFF50, inset 0 0 6px #A98CFF20",
                        textShadow: "0 0 6px #A98CFF90",
                      }
                    : { color: "#A98CFFcc" }
                }
                className={
                  "shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition text-[11.5px] " +
                  (isAdmin ? "border" : "hover:opacity-90 border border-transparent")
                }
              >
                <ShieldCheck className="w-3.5 h-3.5" /> Admin
              </Link>
            )}
          </nav>
        </div>
      </header>
      <ProUpgradeModal open={proOpen} onClose={() => setProOpen(false)} />
    </>
  );
};

export default Header;
